import { Link } from '@tanstack/react-router';

export interface AiQualificationAlertEvent {
  type: 'ai_qualification.alert';
  decision: 'needs_review' | 'error';
  who: string;
  reason: string | null;
  qualificationId: string;
}

/**
 * The live "something needs you" pop-up for AI Lead Review — triggered by
 * an `ai_qualification.alert` SSE event (`RealtimeProvider`), same channel
 * `LeadCelebrationToast` uses for "new lead", but styled as a warning
 * (amber for needs_review, red for a failed AI call) instead of a
 * celebration — this is the opposite mood, something a reviewer needs to
 * act on, not a happy accident to dismiss.
 */
export function AiQualificationAlertToast({ event, onDismiss }: { event: AiQualificationAlertEvent; onDismiss: () => void }) {
  const isFailure = event.decision === 'error';
  return (
    <div className="pointer-events-none fixed inset-x-0 top-20 z-100 flex justify-center">
      <div
        role="alert"
        style={{ animation: 'celebration-pop-in 320ms cubic-bezier(0.2, 0.9, 0.3, 1.3)' }}
        className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-dialog border px-4 py-3.5 shadow-dialog ${isFailure ? 'border-danger-soft bg-danger-soft' : 'border-amber-soft bg-amber-soft'}`}
      >
        <span className={`flex size-9 shrink-0 items-center justify-center rounded-full ${isFailure ? 'bg-danger text-white' : 'bg-amber text-night'}`} aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.3 3.6 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0Z" />
          </svg>
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-ink">{isFailure ? `AI qualification failed for ${event.who}` : `AI Lead Review: ${event.who} needs a decision`}</span>
          {event.reason && <span className="truncate text-[13px] text-text-secondary">{event.reason}</span>}
        </div>
        <Link
          to="/ai-review"
          onClick={onDismiss}
          className="flex h-7 shrink-0 items-center rounded-chip border border-border-strong bg-surface px-2.5 text-xs font-semibold text-ink hover:bg-surface-subtle"
        >
          Review
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex size-7 shrink-0 items-center justify-center rounded-chip text-text-muted hover:bg-black/5"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
